import Cookie from "js-cookie";

export type MobileEventBridge = Pick<
	RallySSO,
	| "initialize"
	| "getAccessToken"
	| "getApp"
	| "getUser"
	| "showAlert"
	| "close"
	| "openLeaderboard"
	| "inviteToGroup"
> & {
	launchExternal: (url: string) => void;
};

declare global {
	// eslint-disable-next-line @typescript-eslint/naming-convention
	interface Window {
		mlbNative: MobileEventBridge;
	}
	const mlbNative: MobileEventBridge;
}

export default class RallySSO {
	protected static readonly NATIVE_BRIDGE_COOKIE_KEY = "native_cookies";
	static nativeBridge: MobileEventBridge | null;

	constructor() {
		RallySSO.nativeBridge = null;
	}

	/**
	 * Check if website has been loaded inside webview.
	 */
	public static BUILD_IS_NATIVE(): boolean {
		if (window.location.href.includes("loc")) {
			return false;
		}
		const searchParams = new URLSearchParams(window.location.search);

		const isNative = Boolean(searchParams.get("isNative"));
		const isNativeCookie = JSON.parse(
			Cookie.get(this.NATIVE_BRIDGE_COOKIE_KEY) || "false"
		) as boolean;

		if (isNative) {
			Cookie.set(this.NATIVE_BRIDGE_COOKIE_KEY, "true");
		}

		return isNativeCookie || isNative;
	}

	static IS_NATIVE = RallySSO.BUILD_IS_NATIVE();

	public async init() {
		try {
			await this.setNativeBridge();
		} catch (error) {
			if (typeof error === "string") {
				throw new Error(`Initialization Error: ${error}`);
			}

			throw new Error(`Initialization Error: ${(error as Error)?.message}`);
		}
	}

	public async initialize(callback: (() => void) | string): Promise<void> {
		return Promise.resolve(RallySSO.nativeBridge?.initialize(callback));
	}

	public async getAccessToken(): Promise<string | null> {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getAccessToken() || null;
		}

		return Promise.resolve(null);
	}

	public async getApp(): Promise<string | null> {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getApp() || null;
		}

		return Promise.resolve(null);
	}

	public getUser(): null {
		if (RallySSO.IS_NATIVE) {
			return RallySSO.nativeBridge?.getUser() || null;
		}

		return null;
	}

	public openLeaderboard(): void {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.openLeaderboard();
		}
	}

	public showAlert(message: string) {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.showAlert(message);
		}
	}

	public static launchExternal(url: string) {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.launchExternal(url);
		}
	}

	public close() {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.close();
		}
	}

	public inviteToGroup(gameId: string, id: string | number) {
		if (RallySSO.IS_NATIVE) {
			RallySSO.nativeBridge?.inviteToGroup(gameId, id);
		}
	}

	/**
	 * Initialize sso for apps
	 * the API is stored in mlbNative global variable. Do not change it to window.pgNative
	 * timeout has been added by MLB request - 20 seconds
	 */
	private setNativeBridge() {
		return new Promise((resolve, reject) => {
			let timer: NodeJS.Timeout;
			let timesCalled = 0;
			const checkState = (): void => {
				if (typeof mlbNative !== "undefined" && !RallySSO.nativeBridge) {
					RallySSO.nativeBridge = mlbNative;

					clearTimeout(timer);
					resolve(RallySSO.nativeBridge);
				} else {
					timesCalled = timesCalled + 1;

					if (timesCalled === 50) {
						clearTimeout(timer);
						reject("Native bridge was not set");
						return;
					}

					timer = setTimeout(checkState, 200);
				}
			};

			checkState();
		});
	}
}
